/**
 * ControlRenderer Component
 * Maps form control types to React components and renders them
 *
 * Type-safe rendering of 88+ control types with proper React patterns
 * - Control registry lookup
 * - Fallback rendering for unknown types
 * - Custom control support
 * - Mode-aware rendering (edit/view/design/admin/preview)
 * - Error and validation support
 * - Event handler propagation
 */

import React, { useMemo } from 'react';
import type { FormControl, OperatingMode as FormOperatingMode } from '@atlas-forms/types-js';
import { getGlobalReactRegistry } from './ReactControlRegistry';
import { getModeConfig } from '../utils/modeConfig';

/**
 * Props for ControlRenderer component
 */
export interface ControlRendererProps {
  /** The control to render */
  control: FormControl;

  /** Current form value */
  value: any;

  /** Change handler */
  onChange: (value: any) => void;

  /** Blur handler */
  onBlur: () => void;

  /** Focus handler */
  onFocus?: () => void;

  /** Validation error message */
  error?: string;

  /** Field has been touched/interacted */
  touched?: boolean;

  /** Field is disabled */
  disabled?: boolean;

  /** Form operating mode */
  mode?: FormOperatingMode;

  /** Custom component registry (optional) */
  registry?: IControlComponentRegistry;
}

/**
 * Control component registry interface
 */
export interface IControlComponentRegistry {
  /**
   * Get a component for a control type
   */
  getComponent(
    type: string,
    mode?: string,
  ): React.ComponentType<ControlRendererProps> | undefined;

  /**
   * Register a custom component
   */
  register(type: string, component: React.ComponentType<ControlRendererProps>): void;

  /**
   * Unregister a component
   */
  unregister(type: string): void;

  /**
   * Check if component is registered
   */
  has(type: string): boolean;

  /**
   * Get all registered components as a map of type → component
   */
  getAll(): Map<string, React.ComponentType<ControlRendererProps>>;
}

/**
 * Default placeholder for unregistered controls
 */
const PlaceholderControl: React.FC<ControlRendererProps> = ({ control, mode }) => (
  <div className="control-renderer__placeholder" style={{
    padding: '12px',
    border: '2px dashed #f59e0b',
    borderRadius: '4px',
    backgroundColor: '#fffbeb',
    color: '#d97706',
  }}>
    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
      Unregistered Control Type
    </div>
    <div style={{ fontSize: '12px' }}>
      Type: <code>{control.type}</code>
      {mode === 'design' && ` (ID: ${control.id})`}
    </div>
  </div>
);

/**
 * Get the global control component registry.
 * Returns the unified ReactControlRegistry singleton.
 * @deprecated Use `getGlobalReactRegistry()` from `./ReactControlRegistry` directly
 *             to also access plugin metadata and the `registerReact()` API.
 */
export const getGlobalControlRegistry = (): IControlComponentRegistry => {
  return getGlobalReactRegistry();
};

/**
 * Reset the global control component registry (for testing).
 * @deprecated Use `resetGlobalReactRegistry()` from `./ReactControlRegistry`.
 */
export { resetGlobalReactRegistry as resetGlobalControlRegistry } from './ReactControlRegistry';

/**
 * ControlRenderer component - renders form controls dynamically
 */
export const ControlRenderer: React.FC<ControlRendererProps> = ({
  control,
  value,
  onChange,
  onBlur,
  onFocus,
  error,
  touched,
  disabled,
  mode = 'edit',
  registry,
}) => {
  // Use provided registry or the global unified ReactControlRegistry
  const activeRegistry = registry ?? getGlobalReactRegistry();

  // Memoize component lookup — pass mode so the registry can return the right variant
  const Component = useMemo(() => {
    const component = activeRegistry.getComponent(control.type, mode);
    return component ?? PlaceholderControl;
  }, [control.type, mode, activeRegistry]);

  // Check if control should be rendered in this mode
  const shouldRender = useMemo(() => {
    const cfg = getModeConfig(mode ?? 'edit');
    // Hidden field visibility
    if (control.hidden && !cfg.showHidden) return false;
    // Mode-specific visibility settings
    if (control.modeVisibilitySettings) {
      const modeKey = (mode ?? 'edit') as keyof typeof control.modeVisibilitySettings;
      if (control.modeVisibilitySettings[modeKey] === false) return false;
    }
    return true;
  }, [control, mode]);

  if (!shouldRender) {
    return null;
  }

  const rendered = (
    <Component
      control={control}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      onFocus={onFocus}
      error={error}
      touched={touched}
      disabled={disabled}
      mode={mode}
      registry={activeRegistry}
    />
  );

  // Only apply responsive hiding in view/preview/admin — designers must see all controls
  if (mode !== 'design' && mode !== 'edit' && control.responsiveVisibility === 'large-only') {
    return <div className="af-responsive--large-only">{rendered}</div>;
  }
  if (mode !== 'design' && mode !== 'edit' && control.responsiveVisibility === 'small-only') {
    return <div className="af-responsive--small-only">{rendered}</div>;
  }
  return rendered;
};

ControlRenderer.displayName = 'ControlRenderer';

export default ControlRenderer;
